import React, { useState, useRef } from 'react';
import { api } from '@/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import {
  Brain, Upload, Loader2, FileText, CheckCircle2, AlertTriangle,
  Building2, Calendar, DollarSign, Gavel, Users, ClipboardList, X
} from 'lucide-react';

function FieldRow({ label, value, icon: Icon }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2 py-1.5 border-b border-slate-100 last:border-0">
      {Icon && <Icon className="h-4 w-4 text-slate-400 mt-0.5 flex-shrink-0" />}
      <div className="min-w-0">
        <p className="text-xs text-slate-500 uppercase font-semibold tracking-wider">{label}</p>
        <p className="text-sm text-slate-800">{String(value)}</p>
      </div>
    </div>
  );
}

export default function ContractExtractor({ projectId, onDataExtracted }) {
  const [extracting, setExtracting] = useState(false);
  const [result, setResult] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const fileInputRef = useRef(null);
  const [extractProgress, setExtractProgress] = useState('');
  const pollRef = useRef(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    setExtracting(true);
    setResult(null);
    setShowDialog(true);
    setExtractProgress('Uploading contract...');

    try {
      const res = await api.files.extractContract(projectId, file);
      const jobId = res.data.job_id;
      
      if (!jobId) {
        setResult(res.data);
        if (res.data.extracted) toast.success('Contract data extracted successfully');
        else toast.error(res.data.error || 'Could not extract data');
        setExtracting(false);
        return;
      }
      
      setExtractProgress('Starting OCR on scanned pages...');
      
      pollRef.current = setInterval(async () => {
        try {
          const jobRes = await api.get(`/extraction-jobs/${jobId}`);
          const job = jobRes.data;
          setExtractProgress(job.progress || 'Processing...');
          
          if (job.status === 'completed') {
            clearInterval(pollRef.current);
            setResult({
              extracted: true,
              filename: job.filename,
              ocr_used: job.ocr_used,
              data: job.data,
            });
            toast.success('Contract data extracted successfully');
            setExtracting(false);
          } else if (job.status === 'failed') {
            clearInterval(pollRef.current);
            setResult({ extracted: false, error: job.error, data: {} });
            toast.error(job.error || 'Extraction failed');
            setExtracting(false);
          }
        } catch (pollErr) {
          console.error('Poll error:', pollErr);
        }
      }, 3000);
      
      setTimeout(() => {
        if (pollRef.current) {
          clearInterval(pollRef.current);
          setExtracting(false);
          setResult({ extracted: false, error: 'Extraction timed out. Please try again.', data: {} });
        }
      }, 300000);
      
    } catch (err) {
      toast.error('Extraction failed');
      setResult({ extracted: false, error: 'Request failed', data: {} });
      setExtracting(false);
    }
  };

  const handleApply = () => {
    if (result?.data) {
      onDataExtracted?.(result.data);
      setShowDialog(false);
      toast.success('Extracted data applied to project');
    }
  };

  const d = result?.data || {};

  return (
    <>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        accept=".pdf,.doc,.docx,.txt"
        className="hidden"
      />
      <Button
        variant="outline"
        size="sm"
        onClick={() => fileInputRef.current?.click()}
        disabled={extracting}
        className="gap-1.5"
        data-testid="extract-contract-btn"
      >
        {extracting ? (
          <><Loader2 className="h-4 w-4 animate-spin" /> Extracting...</>
        ) : (
          <><Brain className="h-4 w-4" /> AI Extract from Contract</>
        )}
      </Button>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogTitle className="text-lg font-barlow font-bold uppercase flex items-center gap-2">
            <Brain className="h-5 w-5" />
            AI Contract Data Extraction
          </DialogTitle>
          <DialogDescription>
            {extracting 
              ? 'Analyzing your contract document with AI...'
              : result?.extracted 
                ? 'Review the extracted data below and click "Apply" to populate your project.'
                : 'Upload a contract document (PDF or DOCX) to extract key data.'}
          </DialogDescription>

          {extracting && (
            <div className="text-center py-8">
              <Loader2 className="h-12 w-12 text-indigo-500 mx-auto mb-3 animate-spin" />
              <p className="text-sm font-medium text-slate-700">{extractProgress || 'Processing...'}</p>
              <p className="text-xs text-slate-400 mt-1">Scanned contracts use OCR — this may take up to 90 seconds</p>
            </div>
          )}

          {result && !extracting && (
            <div className="space-y-4" data-testid="extraction-results">
              {!result.extracted ? (
                <Card className="bg-red-50 border-red-200">
                  <CardContent className="p-4 flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-red-700">Extraction Failed</p>
                      <p className="text-sm text-red-600">{result.error}</p>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <>
                  <Card className="bg-green-50 border-green-200">
                    <CardContent className="p-3 flex items-center gap-2 flex-wrap">
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                      <span className="text-sm font-medium text-green-700">
                        Data extracted from "{result.filename}"
                      </span>
                      {result.ocr_used && (
                        <Badge className="bg-amber-100 text-amber-700 border-amber-200">OCR Scanned</Badge>
                      )}
                      {d.contract_family && (
                        <Badge className="ml-auto bg-slate-900 text-white">{d.contract_family}</Badge>
                      )}
                    </CardContent>
                  </Card>

                  {/* Document sections identified */}
                  {d.document_sections_found?.length > 0 && (
                    <Card className="bg-blue-50 border-blue-200">
                      <CardContent className="p-3">
                        <p className="text-xs font-bold uppercase tracking-wider text-blue-700 mb-1.5">Sections Identified in Document</p>
                        <div className="flex flex-wrap gap-1.5">
                          {d.document_sections_found.map((section, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs border-blue-300 text-blue-700">{section}</Badge>
                          ))}
                        </div>
                        <p className="text-xs text-blue-600 mt-2">Only contract agreement & contract data sections were used for extraction. BOQ/quantities pages were excluded.</p>
                      </CardContent>
                    </Card>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Parties */}
                    <Card>
                      <CardContent className="p-4">
                        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5">
                          <Users className="h-3.5 w-3.5" /> Parties
                        </h4>
                        <FieldRow label="Employer" value={d.employer_name} icon={Building2} />
                        <FieldRow label="Contractor" value={d.contractor_name} icon={Building2} />
                        <FieldRow label={d.contract_family?.startsWith('NEC') ? 'Project Manager' : d.contract_family === 'FIDIC' ? 'Engineer' : 'Principal Agent'} value={d.principal_agent_name} icon={Users} />
                        {d.principal_agent_company && <FieldRow label="PA Company" value={d.principal_agent_company} />}
                      </CardContent>
                    </Card>

                    {/* Contract Info */}
                    <Card>
                      <CardContent className="p-4">
                        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5">
                          <Gavel className="h-3.5 w-3.5" /> Contract Details
                        </h4>
                        <FieldRow label="Contract Family" value={d.contract_family} icon={Gavel} />
                        <FieldRow label="Form / Edition" value={d.contract_form} icon={FileText} />
                        <FieldRow label="Project Description" value={d.project_description} icon={ClipboardList} />
                      </CardContent>
                    </Card>

                    {/* Dates */}
                    <Card>
                      <CardContent className="p-4">
                        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5">
                          <Calendar className="h-3.5 w-3.5" /> Dates
                        </h4>
                        <FieldRow label="Start Date" value={d.start_date} icon={Calendar} />
                        <FieldRow label="Completion Date" value={d.completion_date} icon={Calendar} />
                        <FieldRow label="Construction Period" value={d.construction_period_days ? `${d.construction_period_days} days` : null} />
                        <FieldRow label="Defects Liability" value={d.defects_liability_period} />
                        <FieldRow label="Sectional Completion" value={d.sectional_completion_applies ? 'Yes' : d.sectional_completion_applies === false ? 'No' : null} />
                      </CardContent>
                    </Card>

                    {/* Financial */}
                    <Card>
                      <CardContent className="p-4">
                        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5">
                          <DollarSign className="h-3.5 w-3.5" /> Financial
                        </h4>
                        <FieldRow label="Contract Value" value={d.contract_value ? `${d.currency || 'ZAR'} ${Number(d.contract_value).toLocaleString()}` : null} icon={DollarSign} />
                        <FieldRow label="Penalty Rate / Day" value={d.penalty_rate_per_day} />
                        <FieldRow label="Retention %" value={d.retention_percentage} />
                        <FieldRow label="Guarantee %" value={d.performance_guarantee_percentage} />
                        <FieldRow label="Escalation" value={d.escalation_applies ? 'Yes' : d.escalation_applies === false ? 'No' : null} />
                        <FieldRow label="Payment Terms" value={d.payment_terms} />
                      </CardContent>
                    </Card>
                  </div>

                  {/* Key Clauses */}
                  {d.key_clauses?.length > 0 && (
                    <Card>
                      <CardContent className="p-4">
                        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5">
                          <Gavel className="h-3.5 w-3.5" /> Key Clauses Identified
                        </h4>
                        <div className="space-y-2">
                          {d.key_clauses.map((clause, idx) => (
                            <div key={idx} className="flex items-start gap-2 py-1 border-b border-slate-100 last:border-0">
                              <Badge variant="outline" className="text-xs flex-shrink-0">{clause.clause_number}</Badge>
                              <div>
                                <p className="text-sm font-medium text-slate-700">{clause.title}</p>
                                <p className="text-xs text-slate-500">{clause.summary}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {d.special_conditions_summary && (
                    <Card className="bg-amber-50 border-amber-200">
                      <CardContent className="p-4">
                        <h4 className="text-xs font-bold uppercase tracking-wider text-amber-700 mb-1">Special / Particular Conditions</h4>
                        <p className="text-sm text-amber-800">{d.special_conditions_summary}</p>
                      </CardContent>
                    </Card>
                  )}
                </>
              )}
            </div>
          )}

          {result?.extracted && !extracting && (
            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
              <Button
                onClick={handleApply}
                className="bg-slate-900 hover:bg-slate-800"
                data-testid="apply-extraction-btn"
              >
                <CheckCircle2 className="h-4 w-4 mr-1" />
                Apply to Project
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
